import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { glossary, glossaryCategories } from '../data/glossary';
import { getProduct } from '../data/products';
import { Leaf, Search, X } from 'lucide-react';

const Glossary = () => {
  const [category, setCategory] = useState('All');
  const [query, setQuery] = useState('');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const filtered = useMemo(() => {
    let list = glossary;
    if (category !== 'All') list = list.filter(g => g.category === category);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(g =>
        g.name.toLowerCase().includes(q) ||
        (g.latin && g.latin.toLowerCase().includes(q)) ||
        g.benefits.toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [category, query]);

  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-b from-[#efe9db] to-[#faf7f1]">
        <div className="max-w-5xl mx-auto px-6 py-20 md:py-28 text-center">
          <div className="inline-flex items-center gap-2 uppercase tracking-[0.35em] text-xs text-[#7a8b6b] mb-4">
            <Leaf className="w-4 h-4" /> Botanical Glossary
          </div>
          <h1 className="font-serif-elegant text-5xl md:text-7xl mb-4">Know Your Herbs</h1>
          <p className="text-[#4a4640] max-w-2xl mx-auto leading-relaxed">
            Every plant we use has a story. Learn a little about each botanical
            in our products &mdash; where it comes from, what it does, and why we love it.
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
        {/* Controls */}
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:justify-between border-y border-[#e5decf] py-4 mb-10">
          <div className="relative w-full md:w-80">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6f6a5f]" />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search herbs, oils, benefits..."
              className="w-full pl-9 pr-9 py-2 bg-transparent border border-[#c9c1ad] text-sm focus:outline-none focus:border-[#3a4a3b]"
            />
            {query && (
              <button onClick={() => setQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2" aria-label="Clear">
                <X className="w-4 h-4 text-[#6f6a5f]" />
              </button>
            )}
          </div>
          <div className="text-sm text-[#6f6a5f] whitespace-nowrap">{filtered.length} {filtered.length === 1 ? 'entry' : 'entries'}</div>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2 mb-10">
          {glossaryCategories.map(c => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`px-4 py-1.5 text-xs uppercase tracking-widest border transition-colors ${category === c ? 'bg-[#3a4a3b] border-[#3a4a3b] text-[#faf7f1]' : 'border-[#c9c1ad] text-[#4a4640] hover:border-[#3a4a3b]'}`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Entries */}
        {filtered.length === 0 ? (
          <div className="py-24 text-center text-[#6f6a5f]">
            <Leaf className="w-12 h-12 mx-auto mb-4 text-[#7a8b6b]" />
            <p className="font-serif-elegant text-2xl mb-2">No matches yet</p>
            <p className="text-sm">Try a different search or category.</p>
          </div>
        ) : (
          <ul className="grid md:grid-cols-2 gap-8">
            {filtered.map(entry => (
              <li key={entry.name} className="bg-[#faf7f1] border border-[#e5decf] p-6 hover:shadow-md transition-shadow">
                <div className="flex items-baseline justify-between gap-4 mb-1">
                  <h3 className="font-serif-elegant text-2xl text-[#2f2a24]">{entry.name}</h3>
                  <span className="text-[10px] uppercase tracking-widest text-[#7a8b6b] whitespace-nowrap">{entry.category}</span>
                </div>
                {entry.latin && <div className="font-script text-lg text-[#7a8b6b] mb-3">{entry.latin}</div>}
                <p className="text-sm text-[#4a4640] leading-relaxed mb-4">{entry.benefits}</p>
                {entry.foundIn && entry.foundIn.length > 0 && (
                  <div>
                    <div className="uppercase tracking-widest text-[10px] text-[#6f6a5f] mb-2">Found in</div>
                    <div className="flex flex-wrap gap-1.5">
                      {entry.foundIn.slice(0, 6).map(pid => {
                        const p = getProduct(pid);
                        if (!p) return null;
                        return (
                          <Link
                            key={pid}
                            to={`/products/${pid}`}
                            className="text-xs px-2.5 py-1 border border-[#c9c1ad] hover:border-[#3a4a3b] hover:bg-[#efe9db] transition-colors"
                          >
                            {p.title}
                          </Link>
                        );
                      })}
                      {entry.foundIn.length > 6 && (
                        <span className="text-xs px-2.5 py-1 text-[#6f6a5f]">+{entry.foundIn.length - 6} more</span>
                      )}
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Bottom CTA */}
      <section className="max-w-4xl mx-auto px-6 py-16 text-center">
        <h2 className="font-serif-elegant text-3xl md:text-4xl mb-4">Ready to feel the plants at work?</h2>
        <p className="text-[#4a4640] mb-6">Every one of these botanicals lives inside our small-batch elixirs, scrubs and remedies.</p>
        <Link to="/collections/all" className="btn-primary">Shop the Collection</Link>
      </section>
    </div>
  );
};

export default Glossary;
